'use client';

import { RefObject, useEffect, useRef, useState } from 'react';
import { useSearch } from '@/app/search-context';
import { getOverlayResults, extractText, OverlayResult } from './actions/list';
import { useAlmostInView } from './hooks/almost-in-view';
import { NavLink } from '@/components/nav-link';

function highlight(text: string, query: string) {
  if (!query) return text;
  const words = query.split(/\s+/).filter(Boolean);
  if (words.length === 0) return text;
  const escaped = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, `\\$&`));
  const regex = new RegExp(`(${escaped.join(`|`)})`, `gi`);
  const parts = text.split(regex);
  const match = new RegExp(`^(?:${escaped.join(`|`)})$`, `i`);
  return parts.map((part, i) =>
    match.test(part)
      ? <mark key={i} className="search-highlight">{part}</mark>
      : part
  );
}

function SearchInput({ value, onChange, onClear, onFocus, placeholder, inputRef, }: {
  value: string
  onChange: (value: string) => void
  onClear: () => void
  onFocus?: () => void
  placeholder: string
  inputRef?: RefObject<HTMLInputElement>
}) {
  return (
    <div className="search-input-wrap">
      <input
        ref={inputRef}
        type="text"
        className="search"
        autoCapitalize="off"
        value={value}
        onFocus={onFocus}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {value && (
        <button
          className="search-clear-button"
          onPointerDown={e => { e.preventDefault(); onClear(); }}
          aria-label="Clear search"
        >
          &times;
        </button>
      )}
    </div>
  );
}

function SearchResult({ result, query, onSelect, }: { result: OverlayResult, query: string, onSelect: () => void }) {
  const isVideo = result.type === `video`;
  const item = result.item as any;
  const name = isVideo ? item.title : item.name;
  const thumb = isVideo ? item.poster?.signedUrls.jpeg[0]?.url : item.portrait?.signedUrls.jpeg[0]?.url;
  const slug = isVideo ? `/video/${item.slug}` : `/model/${item.slug}`;
  const desc = extractText(item.description);
  const snippet = desc.length > 160 ? desc.slice(0, 160).trimEnd() + `…` : desc;

  return (
    <NavLink href={slug} className="search-overlay-item" onNavigate={onSelect}>
      {thumb && <img className="search-overlay-thumb" src={thumb} alt="" />}
      <div className="search-overlay-info">
        <span className="search-overlay-type">{isVideo ? `Video` : `Model`}</span>
        <div className="search-overlay-title">{highlight(name, query)}</div>
        {item.tags && item.tags.length > 0 && (
          <div className="search-overlay-tags">
            {item.tags.map((t: { id: number; name: string }) => (
              <span key={t.id} className="search-overlay-tag">{highlight(t.name, query)}</span>
            ))}
          </div>
        )}
        {snippet && <div className="search-overlay-desc">{highlight(snippet, query)}</div>}
      </div>
    </NavLink>
  );
}

export default function SearchClient({ className, placeholder, }: { className: string, placeholder?: string }) {
  const { allVideos, allModels, dataLoading, pageQuery, setPageQuery, } = useSearch();
  const pageFilter = className.includes(`page-filter`);
  const [query, setQuery,] = useState(``);
  const [open, setOpen,] = useState(false);
  const [typeFilter, setTypeFilter,] = useState<`all` | `video` | `model`>(`all`);
  const [visibleCount, setVisibleCount,] = useState(20);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [sentinelRef, sentinelInView,] = useAlmostInView(0, `100px`);

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener(`pointerdown`, onPointerDown);
    return () => document.removeEventListener(`pointerdown`, onPointerDown);
  }, []);

  if (pageFilter) {
    return (
      <div className={className}>
        <SearchInput
          value={pageQuery}
          onChange={setPageQuery}
          onClear={() => setPageQuery(``)}
          placeholder={dataLoading ? `Loading…` : (placeholder || `Search…`)}
        />
      </div>
    );
  }

  const overlayOpen = open && query.length > 0;
  const ready = !dataLoading && allVideos !== null && allModels !== null;

  const results = ready
    ? getOverlayResults(allVideos!, allModels!, query, typeFilter)
    : [];

  // Reset visible count when query or type filter changes
  useEffect(() => { setVisibleCount(20); }, [query, typeFilter,]);

  // Load more when sentinel scrolls into view
  useEffect(() => {
    if (sentinelInView && visibleCount < results.length) {
      setVisibleCount(v => v + 20);
    }
  }, [sentinelInView, results.length, visibleCount,]);

  const visibleResults = results.slice(0, visibleCount);

  return (
    <div ref={ref} className={className}>
      <SearchInput
        inputRef={inputRef}
        value={query}
        onChange={v => { setQuery(v); setOpen(true); }}
        onClear={() => { setQuery(``); setOpen(true); inputRef.current?.focus(); }}
        onFocus={() => setOpen(true)}
        placeholder={dataLoading ? `Loading…` : `Search…`}
      />

      {overlayOpen && (
        <div className="search-overlay">
          <div className="search-overlay-tabs">
            {([`video`, `model`, `all`,] as const).map(t => (
              <button
                key={t}
                className={`search-overlay-tab${typeFilter === t ? ` active` : ``}`}
                onPointerDown={e => { e.preventDefault(); setTypeFilter(t); }}
              >
                {t === `all` ? `All` : t === `video` ? `Videos` : `Models`}
              </button>
            ))}
          </div>

          {!ready && <div className="search-overlay-loading">Loading…</div>}

          {ready && results.length === 0 && (
            <div className="search-overlay-empty">No results</div>
          )}

          {ready && visibleResults.map(r => (
            <SearchResult
              key={`${r.type}-${r.item.slug}`}
              result={r}
              query={query}
              onSelect={() => { setOpen(false); setQuery(``); }}
            />
          ))}

          {ready && visibleCount < results.length && (
            <div ref={sentinelRef} className="search-overlay-loading">
              <div className="animate-spin rounded-full h-6 w-6 border-4 border-t-transparent" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
